import React from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './Uplifts.css';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../../../components/foundations/Icon';
import { NA_VALUE, PercentageFormatter, UPLIFTS_ICON_NAMES } from '../consts';
import QuestionMark from '../../../../components/widgets/QuestionMark';
import { GROUPS_LABELS } from '../../Wizard/consts';
import Tooltip from '../../../../components/molecules/Tooltip';
import { TRANSLATION_PREFIX_UPLIFT } from '../reportingUtils';

const SignificanceBadge = ({ uplifts }) => {
    if (!uplifts.some(uplift => uplift.value !== NA_VALUE)) return null;
    const isSignificant = uplifts.some(uplift => uplift.isSignificant);
    const statusKey = isSignificant ? 'SIGNIFICANT' : 'NOT_SIGNIFICANT';

    return (
        <div className={classNames(css.pvalue, isSignificant ? css.significant : css.notSignificant)}>
            <div className={classNames(css.pvalueLabel, isSignificant ? css.significant : css.notSignificant)}>
                <Translate id={`${TRANSLATION_PREFIX_UPLIFT}.${statusKey}`} />
            </div>
            <Tooltip
                titleTranslationKey={`${TRANSLATION_PREFIX_UPLIFT}.${statusKey}_TOOLTIP`}
                wrapperClass={css.tooltipWrapper}
            >
                <Icon name="help-circle" size={IconSizes.SMALL} />
            </Tooltip>
        </div>
    );
};

SignificanceBadge.propTypes = {
    uplifts: PropTypes.arrayOf(
        PropTypes.shape({
            isSignificant: PropTypes.bool,
        })
    ).isRequired,
};

const UPLIFT_ICONS = {
    [UPLIFTS_ICON_NAMES.UP]: {
        iconName: UPLIFTS_ICON_NAMES.UP,
        variant: IconVariants.SUCCESS,
        className: css.positive,
    },
    [UPLIFTS_ICON_NAMES.DOWN]: {
        iconName: UPLIFTS_ICON_NAMES.DOWN,
        variant: IconVariants.ERROR,
        className: css.negative,
    },
    [UPLIFTS_ICON_NAMES.FORK]: {
        iconName: UPLIFTS_ICON_NAMES.FORK,
        variant: IconVariants.WARNING,
        className: css.fork,
    },
};

const isValidUpliftNumber = uplift => typeof uplift.value === 'number' && !Number.isNaN(uplift.value);

const getUpliftIcon = uplifts => {
    // if the first line is positive and the second is negative, we need to use a different icon
    const doesTwoLinesHaveSameSign = uplifts.length > 1 && uplifts[0].value * uplifts[1].value < 0;
    if (!uplifts[0].value) return null;

    const { iconName, variant, className } = doesTwoLinesHaveSameSign
        ? UPLIFT_ICONS[UPLIFTS_ICON_NAMES.FORK]
        : uplifts[0].value > 0
        ? UPLIFT_ICONS[UPLIFTS_ICON_NAMES.UP]
        : UPLIFT_ICONS[UPLIFTS_ICON_NAMES.DOWN];
    return <Icon name={iconName} type={IconTypes.TABLER} variant={variant} className={className} />;
};

const Uplifts = ({ networkUplifts, isDuplicateNetworkSelected }) => {
    const isTwoLines = networkUplifts.some(singleNetwork => singleNetwork.uplifts.length > 1);
    return (
        <div className={css.metricsContainer}>
            {networkUplifts.map((singleNetwork, networkIndex) => (
                <div
                    key={singleNetwork.network + singleNetwork.uplifts[0].label}
                    className={`${css.metricCard} ${isTwoLines ? css.twoLinesMetricCard : ''}`}
                >
                    <div className={css.networkInfo}>
                        <div className={css.networkIconContainer}>
                            <img className={css.networkIcon} src={singleNetwork.adnetworkLogoPath} alt="" />
                            <span className={css.networkName}>{singleNetwork.networkDisplayName}</span>
                            {isDuplicateNetworkSelected && (
                                <span className={css.duplicateNetworkTitle}>
                                    ({isDuplicateNetworkSelected && GROUPS_LABELS[networkIndex < 2 ? 0 : 1]})
                                </span>
                            )}
                        </div>
                        {isValidUpliftNumber(singleNetwork.uplifts[0]) && (
                            <div className={css.trendIcon}>{getUpliftIcon(singleNetwork.uplifts)}</div>
                        )}
                    </div>
                    <div className={css.uplifts}>
                        {singleNetwork.uplifts.map((uplift, index) => {
                            return (
                                <div key={uplift.value + uplift.label} className={css.upliftTitleHeader}>
                                    <div className={css.upliftTitleContainer}>
                                        <div className={css.metricLabel}>{uplift.label}</div>
                                        {index === 0 && (
                                            <QuestionMark
                                                tooltipClassName={css.tooltipContainer}
                                                message={uplift.tooltip}
                                            />
                                        )}
                                    </div>
                                    <div
                                        className={`${css.metricValue} ${
                                            isValidUpliftNumber(uplift)
                                                ? uplift.value >= 0
                                                    ? css.positive
                                                    : css.negative
                                                : css.nautral
                                        }`}
                                    >
                                        {isValidUpliftNumber(uplift) ? PercentageFormatter.format(uplift.value) : 'N/A'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <SignificanceBadge uplifts={singleNetwork.uplifts} />
                </div>
            ))}
        </div>
    );
};

Uplifts.propTypes = {
    isDuplicateNetworkSelected: PropTypes.bool,
    networkUplifts: PropTypes.arrayOf(
        PropTypes.shape({
            uplifts: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.number })),
            network: PropTypes.string,
            networkDisplayName: PropTypes.string,
            adnetworkLogoPath: PropTypes.string,
            size: PropTypes.number,
        })
    ),
};

export default withLocalize(Uplifts);
